"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/ChatOnboarding/Steps/DiagnosisUnlockFlow/LoadingStep/DiagnosisUnlockLoadingStep";

import { useCallback, useMemo } from "react";
import { Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { LoadingRipples } from "../../LoadingStep/LoadingRipples";
import { LoadingTitleDots } from "../../LoadingStep/LoadingTitleDots";
import { useIsPc } from "@/src/hooks";
import type { DiagnosisUnlockStep1Chrome } from "../types";
import { resolveStepProgressLabels } from "./diagnosisUnlockFourStep.contract";
import { DiagnosisUnlockStepProgress } from "./DiagnosisUnlockStepProgress";
import { useDiagnosisUnlockSimpleLoading } from "./useDiagnosisUnlockSimpleLoading";
import { useDiagnosisUnlockStepProgress } from "./useDiagnosisUnlockFourStepProgress";
import { DiagnosisUnlockLoadingHeroIcon } from "./DiagnosisUnlockLoadingHeroIcon";
import {
  DIAGNOSIS_UNLOCK_LOADING_LAYOUT,
  DIAGNOSIS_UNLOCK_LOADING_LAYOUT_FIGMA,
} from "./diagnosisUnlockLoadingLayout.contract";
import { AppImage } from "@/src/components/NextImage";

const I18n = I18nUtil.createScoped(Translations);

export type DiagnosisUnlockLoadingStepProps = {
  chrome: DiagnosisUnlockStep1Chrome;
  backgroundSrc: string;
  /** 步骤播完或简单 Loading 结束 → 进入 Step2 */
  onComplete: () => void;
  /** 顶栏关闭 dismiss — 仅 chrome 允许 close 时传入 */
  onDismiss?: () => void;
};

/**
 * DiagnosisUnlock Step1 — 假 Loading。
 *
 * | `chrome.loading.showStepProgress` | UI | 结束条件 |
 * |---|---|---|
 * | 默认四步 `string[]` | 步骤 progress 列表 | 逐步播完 → `onComplete` |
 * | 自定义 `string[]` | 同上，行数随数组长度 | 同上 |
 * | `false` | 标题 + 涟漪 + 省略号 | 默认 3s → `onComplete` |
 */
export function DiagnosisUnlockLoadingStep({
  chrome,
  backgroundSrc,
  onComplete,
  onDismiss,
}: DiagnosisUnlockLoadingStepProps) {
  const [isPc] = useIsPc();
  const stepLabels = useMemo(
    () => resolveStepProgressLabels(chrome.showStepProgress),
    [chrome.showStepProgress],
  );
  const hasStepProgress = stepLabels != null && stepLabels.length > 0;
  const topNav = chrome.topNav;

  const handleComplete = useCallback(() => {
    onComplete();
  }, [onComplete]);

  const { activeIndex } = useDiagnosisUnlockStepProgress({
    labels: stepLabels ?? [],
    paused: !hasStepProgress,
    onComplete: handleComplete,
  });

  useDiagnosisUnlockSimpleLoading({
    enabled: !hasStepProgress,
    onComplete: handleComplete,
  });

  const showBack =
    !isPc && topNav.variant === "back" && topNav.behavior === "previous_step";
  const showClose =
    (isPc && topNav.variant === "close") ||
    (!isPc && topNav.variant === "close");

  const layout = isPc
    ? DIAGNOSIS_UNLOCK_LOADING_LAYOUT.pc
    : DIAGNOSIS_UNLOCK_LOADING_LAYOUT.h5;

  return (
    <div
      className="relative h-full w-full min-h-0 overflow-hidden md:h-[674px] md:min-h-[674px] md:max-h-[674px]"
      data-testid="diagnosis-unlock-loading-step"
      data-figma-node-id={
        isPc
          ? DIAGNOSIS_UNLOCK_LOADING_LAYOUT_FIGMA.pcModal
          : DIAGNOSIS_UNLOCK_LOADING_LAYOUT_FIGMA.h5Page
      }
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <AppImage
          src={backgroundSrc}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          width={393}
          height={852}
          loading="eager"
          decoding="async"
        />
      </div>
      <LoadingRipples
        playing
        centerHorizontally
        centerY={layout.rippleCenterY}
        ringPeakOpacity={0.3}
      />
      {showBack ? (
        <div className="relative z-10 flex shrink-0 px-4 py-2 pt-safe">
          <button
            type="button"
            onClick={onDismiss}
            className="flex size-10 items-center justify-center rounded-[20px] bg-background_solid_neutral_primary"
            data-testid="diagnosis-unlock-loading-back"
            aria-label="Back"
          >
            <Icon
              name="left-arrow-alt"
              size={20}
              className="text-content_primary"
            />
          </button>
        </div>
      ) : null}
      {showClose && onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          className={cn(
            "absolute z-20 flex size-10 items-center justify-center rounded-full",
            isPc ? "right-8 top-8" : "right-4 top-4",
          )}
          data-testid="diagnosis-unlock-loading-close"
          aria-label="Close"
        >
          <Icon
            name="close"
            size={20}
            className="text-content_inverse_primary"
          />
        </button>
      ) : null}
      <div
        className="absolute left-1/2 z-10 flex w-[calc(100%-48px)] max-w-[345px] -translate-x-1/2 flex-col items-center gap-10"
        style={{ top: layout.contentTopPx }}
        data-testid="diagnosis-unlock-loading-content"
        data-figma-node-id={
          isPc
            ? DIAGNOSIS_UNLOCK_LOADING_LAYOUT_FIGMA.pcContentFrame
            : DIAGNOSIS_UNLOCK_LOADING_LAYOUT_FIGMA.h5ContentFrame
        }
      >
        <div className="flex w-full flex-col items-center gap-10">
          <DiagnosisUnlockLoadingHeroIcon />
          <p
            className="headline_regular_m w-full text-center text-content_inverse_primary"
            data-testid="diagnosis-unlock-loading-title"
            data-figma-node-id="90772:39835"
          >
            {I18n.t("I'm cross-checking your portfolio.")}
            {!hasStepProgress ? <LoadingTitleDots /> : null}
          </p>
        </div>

        {hasStepProgress && stepLabels ? (
          <DiagnosisUnlockStepProgress
            labels={stepLabels}
            activeIndex={activeIndex}
          />
        ) : null}
      </div>
    </div>
  );
}
